import React, { useState, useEffect, useRef } from 'react';
import './Header.css';

const HeaderSearch = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState({ schools: [], products: [] });
  const [showResults, setShowResults] = useState(false); // State to control visibility of results
  const resultsRef = useRef(null); // Ref for the results container

  // Get `smi` from localStorage
  const smi = localStorage.getItem('SMI') || '';

  const handleSearch = async () => {
    if (searchQuery.length <= 3) {
      setResults({ schools: [], products: [] });
      setShowResults(false); // Hide results if query is too short
      return;
    }

    setIsLoading(true);

    try {
      const formData = new FormData();
      formData.append('searchterm', searchQuery);
      formData.append('searchtype', 'school_and_product');
      formData.append('smi', smi); // Include `smi` in the request payload

      const response = await fetch('/getsearchdata', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to fetch search results');
      }

      const data = await response.json();

      // Convert products object to an array
      const productsArray = data.products
        ? Object.keys(data.products).map((key) => data.products[key])
        : [];

      setResults({
        schools: Array.isArray(data.schools) ? data.schools : [],
        products: productsArray,
      });

      setShowResults(true); // Show results after fetching
    } catch (error) {
      console.error('Error fetching search results:', error);
      setResults({ schools: [], products: [] });
      setShowResults(false); // Hide results on error
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);

    if (value.length > 3) {
      handleSearch();
    } else {
      setShowResults(false); // Hide results if query is too short
    }
  };

  const handleClickOutside = (event) => {
    // Check if the click is outside the results container
    if (resultsRef.current && !resultsRef.current.contains(event.target)) {
      setShowResults(false); // Hide results
    }
  };

  useEffect(() => {
    // Add event listener for clicks outside
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      // Cleanup event listener on component unmount
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleViewAllProducts = () => {
    // Redirect to the products search results page
    window.location.href = `/shop/catalogsearch/result/?q=${encodeURIComponent(searchQuery)}#products`;
  };

  const handleViewAllSchools = () => {
    // Redirect to the schools search results page
    window.location.href = `/shop/catalogsearch/result/?q=${encodeURIComponent(searchQuery)}#schools`;
  };

  return (
    <div className="search-group">
      <div className="header-search-container">
        <input
          type="text"
          placeholder="Search for your school or products..."
          value={searchQuery}
          onChange={handleInputChange}
        />
        <button type="button" className="search-btn">
          <img
            alt="Search"
            src="https://d3qsmzzpeeacu6.cloudfront.net/s3fs-public/media/balfour/search.svg"
          />
        </button>
      </div>

      {isLoading && <div className="dbalfour-loader" />}

      {showResults && (results.schools.length > 0 || results.products.length > 0) && (
        <div className="search-results-container" ref={resultsRef}>
          <div className="search-results-header">
            {/* Render tabs conditionally */}
            <div className="schools-tab">SCHOOLS</div>
            <div className="products-tab">PRODUCTS</div>
          </div>
          
          <div className="search-results-content">
            {/* Render Schools section */}
            <div className="schools-column">
              {results.schools.map((school, index) => (
                <a key={index} href={school.AffinityUrl} className="school-item">
                  <div className="school-name">{school.SchoolName}</div>
                  <div className="school-location">
                    {school.City}, {school.State}
                  </div>
                </a>
              ))}
              {results.schools.length > 0 && (
                <button
                  className="view-all"
                  onClick={handleViewAllSchools}
                >
                  View All
                </button>
              )}
            </div>

            <div className="products-column">
              {results.products.map((product, index) => (
                <a key={index} href={product.product_url} className="product-item">
                  <img
                    src={product.product_image_small}
                    alt={product.product_name}
                    className="product-image"
                  />
                  <div className="product-details">
                    <div className="product-name">{product.product_name}</div>
                    <div className="product-price">{product.product_price}</div>
                  </div>
                </a>
              ))}
              {results.products.length > 0 && (
                <button
                  className="view-all"
                  onClick={handleViewAllProducts}
                >
                  View All
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HeaderSearch;